import {
  Badge,
  Button,
  Col,
  Container,
  Grid,
  Icon,
  NetworkIcon,
  Radio,
  Tooltip,
  Typography,
} from 'gu-ui-kit'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import React, { useMemo, useRef } from 'react'
import { ROUTES } from 'constants/routes'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import { createPath } from 'history'
import qs from 'qs'
import prepareFilterParams from 'utils/prepareFilterParams'
import pick from 'lodash/pick'
import formatMoney, { formatByLocale } from 'utils/formatMoney'
import { useSelector } from 'react-redux'
import { StatusBadge } from 'modules/Statistics/subcomponents/StatusBadge'
import networkIconNamesMap from 'modules/Statistics/networkIconNamesMap'
import EditableField from 'components/EditableField'
import uniq from 'lodash/uniq'
import AccountManager from 'modules/Statistics/Main/subcomponents/AccountManager'
import GroupManager from 'modules/Statistics/Main/subcomponents/GroupManager'

export const useTableConfig = ({
  groups = [],
  groupBy,
  filters,
  summary,
  sorting,
  setSorting,
  columnVisibility,
  setColumnVisibility,
  className = '',
  onFetchSubRows = () => {},
  showedGroupOnChartId,
  setShowedGroupOnChartId,
  handleAccountAttach = () => {},
  handleChangeGroup = () => {},
  isDemoMode = false,
}) => {
  const i18n = useSelector(({ app }) => app.i18n)
  const userId = useSelector(({ app }) => app.user?.id)

  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()

  const summaryRef = useRef(summary)
  summaryRef.current = summary
  const handleAccountAttachRef = useRef(handleAccountAttach)
  handleAccountAttachRef.current = handleAccountAttach
  const handleChangeGroupRef = useRef(handleChangeGroup)
  handleChangeGroupRef.current = handleChangeGroup
  const showedGroupOnChartIdRef = useRef(showedGroupOnChartId)
  showedGroupOnChartIdRef.current = showedGroupOnChartId
  const setShowedGroupOnChartIdRef = useRef(setShowedGroupOnChartId)
  setShowedGroupOnChartIdRef.current = setShowedGroupOnChartId
  const onFetchSubRowsRef = useRef(onFetchSubRows)
  onFetchSubRowsRef.current = onFetchSubRows

  const columns = useMemo(() => {
    switch (groupBy) {
      case 'accounts':
        return [
          {
            id: 'account.login',
            header: t('statistics::table.columns.header.account'),
            accessorKey: 'account.login',
            enableHiding: false,
            className: 'login-cell sticky',
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: { account = {} },
              },
              // eslint-disable-next-line react/prop-types
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={account.login}
              >
                <div className={'overflow'}>
                  {/* eslint-disable-next-line react/prop-types */}
                  {account.network !== 'vkads' &&
                  // eslint-disable-next-line react/prop-types
                  NETWORK_PREFIX_MAP[account.network] ? (
                    <Link
                      className={styles['navlink']}
                      to={createPath({
                        pathname: pathGen(ROUTES.STATISTICS_ACCOUNT, {
                          // eslint-disable-next-line react/prop-types
                          network: account.network,
                          // eslint-disable-next-line react/prop-types
                          externalId: account.id,
                        }),
                        search: qs.stringify(
                          prepareFilterParams(
                            pick(filters, ['date_group_period', 'date_from', 'date_to']),
                          ),
                          { arrayFormat: 'brackets' },
                        ),
                      })}
                    >
                      {account.login}
                    </Link>
                  ) : (
                    <span className={styles['login']}>{account.login}</span>
                  )}
                </div>
              </Container>
            ),
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.accounts', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'account.network',
            header: t('statistics::table.columns.header.network'),
            tooltip: t('statistics::table.columns.header.network_tooltip'),
            accessorKey: 'account.network',
            className: 'network-cell sticky',
            enableHiding: false,
            size: 152,
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: {
                  // eslint-disable-next-line react/prop-types
                  account: { network } = {},
                },
              },
            }) => (
              <NetworkIcon
                name={networkIconNamesMap[network] ?? network}
                size={'m'}
                width={32}
                rounded
              />
            ),
          },
          {
            id: 'account.groups',
            header: t('statistics::table.columns.header.groups'),
            enableHiding: false,
            size: 192,
            accessorKey: 'account.groups',
            cell: ({
              row: {
                original: { account },
              },
            }) => {
              if (!account?.groups || !groups) return null

              return account?.groups.length > 0 ? (
                <Grid gap={8}>
                  {account.groups.map(({ id, name }) => (
                    <Badge
                      title={name}
                      className={styles['group-name-badge']}
                      key={id ?? name}
                    >
                      {name}
                    </Badge>
                  ))}
                  <AccountManager
                    isDemoMode={isDemoMode}
                    ButtonComponent={() => (
                      <Button
                        color={'Interface-Seventh'}
                        startIcon={<Icon name={'settings'} />}
                        variant={'ghost'}
                        grey
                        size={'s'}
                      />
                    )}
                    account={account}
                    groups={groups}
                    onApply={(...args) => handleAccountAttachRef.current(...args)}
                  />
                </Grid>
              ) : (
                <AccountManager
                  isDemoMode={isDemoMode}
                  groups={groups}
                  account={account}
                  onApply={(...args) => handleAccountAttachRef.current(...args)}
                  ButtonComponent={() => (
                    <Button
                      noWrap
                      variant={'ghost'}
                      link
                    >
                      <Typography
                        variant="Body-Forms-15"
                        color={'Label-GU-Primary'}
                      >
                        {t('statistics::table.assign_group')}
                      </Typography>
                    </Button>
                  )}
                />
              )
            },
          },
          {
            id: 'account.active',
            header: t('statistics::table.columns.header.status'),
            accessorKey: 'account.active',
            size: 192,
            enableHiding: false,
            cell: ({
              row: {
                original: { account: { active } = {} },
              },
            }) => <StatusBadge status={active ? 'active' : 'inactive'} />,
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            //size: 120,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summaryRef.current.impressions)}</b>,
          },
          {
            id: filters.include_vat ? 'cost_with_vat' : 'cost',
            header: t(
              `statistics::table.columns.header.${filters.include_vat ? 'cost_with_vat' : 'cost'}`,
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current[filters.include_vat ? 'cost_with_vat' : 'cost'], i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue'),
            accessorKey: 'revenue',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current.revenue, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',

            size: 120,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summaryRef.current.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',

            size: 100,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }) + ' %',
            footer: () => (
              <b>{formatByLocale(summaryRef.current.ctr, i18n, { maximumFractionDigits: 2 }) + ' %'}</b>
            ),
          },
          {
            id: 'avg_click_price',
            header: t('statistics::table.columns.header.avg_click_price'),
            tooltip: t('statistics::table.columns.header.avg_click_price_tooltip'),
            accessorKey: 'avg_click_price',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current.avg_click_price, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',

            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summaryRef.current.conversions)}</b>,
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }) + ' %',
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current.conversion_rate, i18n, {
                  maximumFractionDigits: 2,
                }) + ' %'}
              </b>
            ),
          },
        ]

      case 'networks':
        return [
          {
            id: 'network',
            header: t('statistics::table.columns.header.network'),
            tooltip: t('statistics::table.columns.header.network_tooltip'),
            accessorKey: 'network',
            className: 'network-cell sticky',
            size: 75,
            enableHiding: false,
            cell: ({ cell }) => (
              <NetworkIcon
                name={networkIconNamesMap[cell.getValue()] ?? cell.getValue()}
                size={'m'}
                width={32}
                rounded
              />
            ),
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.networks', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',

            size: 160,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summaryRef.current.impressions)}</b>,
          },
          {
            id: filters.include_vat ? 'cost_with_vat' : 'cost',
            header: t(
              'statistics::table.columns.header.' +
                (filters.include_vat ? 'cost_with_vat' : 'cost'),
            ),
            accessorKey: filters?.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 180,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current[filters.include_vat ? 'cost_with_vat' : 'cost'], i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue'),
            accessorKey: 'revenue',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current.revenue, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',

            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summaryRef.current.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => <b>{formatByLocale(summaryRef.current.ctr, i18n, { maximumFractionDigits: 2 })}</b>,
          },
          {
            id: 'avg_click_price',
            header: t('statistics::table.columns.header.avg_click_price'),
            tooltip: t('statistics::table.columns.header.avg_click_price_tooltip'),
            accessorKey: 'avg_click_price',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current.avg_click_price, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',

            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            footer: () => <b>{formatByLocale(summaryRef.current.conversions)}</b>,
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',

            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {formatByLocale(summaryRef.current.conversion_rate, i18n, {
                  maximumFractionDigits: 2,
                })}
              </b>
            ),
          },
        ]

      case 'groups':
        return [
          {
            id: 'expand',
            header: () => ' ',
            className: 'sticky',
            enableHiding: false,
            cell: ({ row }) =>
              !row.getCanExpand() || row.depth > 0 ? null : (
                <Grid
                  wrap={'nowrap'}
                  justifyContent="start"
                  alignItems="center"
                >
                  <Grid
                    fluid={false}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button
                      noLabel
                      startIcon={<Icon name={row.getIsExpanded() ? 'arrow-up' : 'arrow1-down'} />}
                      title={t('agency::list.icon_title', {
                        context: row.getIsExpanded() ? 'close' : 'open',
                      })}
                      color={row.getIsExpanded() ? 'Interface-Nine' : 'Interface-Seventh'}
                      bgColor={row.getIsExpanded() ? 'Background-Tertiary' : 'none'}
                      bgColorHover={row.getIsExpanded() ? 'Background-Tertiary' : 'none'}
                      bgColorActive={row.getIsExpanded() ? 'Background-Tertiary' : 'none'}
                      borderColorActive={row.getIsExpanded() ? 'Interface-Nine' : 'none'}
                      borderColor={row.getIsExpanded() ? 'Interface-Nine' : 'none'}
                      borderColorHover={'Interface-Nine'}
                      size={'m'}
                      onClick={() => {
                        row.toggleExpanded(!row.getIsExpanded())
                        const subRows = row.original?.subRows || row.original?.accounts
                        const hasSubRows = Array.isArray(subRows) && subRows.length > 0
                        const isLoading = Boolean(row.original?.subRowsLoading)
                        if (!hasSubRows && !isLoading) {
                          onFetchSubRowsRef.current(row.original.id)
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              ),
          },
          {
            id: 'chart-dot',
            header: '',
            enableHiding: false,
            className: 'sticky',
            cell: ({ row }) =>
              !row.getCanExpand() || row.depth > 0 ? null : (
                <Grid className={styles['chart-id-switcher-container']}>
                  <Tooltip title={t('statistics::table.show_group_on_chart')}>
                    <Radio
                      checked={showedGroupOnChartIdRef.current === row.original?.group?.id}
                      onClick={() => setShowedGroupOnChartIdRef.current(row.original?.group?.id)}
                    />
                  </Tooltip>
                </Grid>
              ),
            // footer: ' ',
          },
          {
            id: 'group',
            header: t('statistics::table.columns.header.group'),
            accessorFn: (row) =>
              row?.group?.title || row?.group?.name || row?.group_title || row?.title || row?.name,
            enableHiding: false,
            className: 'group-name-cell sticky',
            cell: ({
              // eslint-disable-next-line react/prop-types
              row,
              // eslint-disable-next-line react/prop-types
              cell,
            }) => {
              if (row.depth > 0) return null

              const title = cell.getValue()
              const id = row.original?.group?.id || ''

              return (
                <Typography
                  variant={'Body-Forms-15'}
                  color={'Label-Fifth'}
                  className={styles['group-name-editable']}
                  component={
                    <EditableField
                      DisabledButtonComponent={({ className }) => (
                        <Tooltip title={t('statistics::tooltips.not_available_for_demo')}>
                          <Button
                            noLabel
                            variant={'secondary'}
                            link
                            disabled
                            size={'s'}
                            startIcon={<Icon name={'edit'} />}
                            className={className}
                            noHover
                          />
                        </Tooltip>
                      )}
                      disabled={isDemoMode}
                      name={title}
                      login={title}
                      id={id}
                      apply={(id, value) =>
                        handleChangeGroupRef.current({
                          id,
                          newGroupData: {
                            name: value,
                            account_ids: row.original?.accounts.map((item) => item.account.id),
                          },
                        })
                      }
                    >
                      {title}
                    </EditableField>
                  }
                />
              )
            },
          },
          {
            id: 'network',
            header: t('statistics::table.columns.header.network'),
            tooltip: t('statistics::table.columns.header.network_tooltip'),
            accessorFn: (row) => row?.networks || row?.network || row?.account?.network,
            className: 'sticky',
            enableHiding: false,
            cell: ({ row }) => {
              const accounts = row.original?.accounts ?? [row.original]

              const networks = uniq(accounts.map(({ account }) => account?.network))

              return networks.length > 0 ? (
                <Grid
                  gap={6}
                  wrap={false}
                  alignItems={'center'}
                  className={styles['networks-icons']}
                >
                  {networks.map((network) => (
                    <NetworkIcon
                      key={network}
                      name={networkIconNamesMap[network] ?? network}
                      size={'m'}
                      width={32}
                      rounded
                    />
                  ))}
                </Grid>
              ) : null
            },
          },
          {
            id: 'account.login',
            header: t('statistics::table.columns.header.account'),
            accessorFn: (row) => row?.account?.login || row?.account_login || row?.login,
            enableHiding: false,
            className: 'account-name-cell sticky',
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: { group, account = {}, accounts },
                depth,
              },
            }) => (
              <Grid
                gap={8}
                wrap={'nowrap'}
                justifyContent="start"
                alignItems="center"
                className={styles['account-name-container']}
              >
                {depth === 0 ? (
                  <>
                    <Col
                      flexGrow={0}
                      className={styles['account-name']}
                      data-content={t('statistics::table.groups.group_accounts')}
                    >
                      <Typography
                        variant={'Body-Forms-15'}
                        component={'div'}
                      >
                        {t('statistics::table.groups.group_accounts')}
                      </Typography>
                    </Col>
                    <Col
                      flexGrow={1}
                      className={styles['account-name__control-opener']}
                    >
                      <GroupManager
                        {...group}
                        isDemoMode={isDemoMode}
                        onApply={(...args) => handleChangeGroupRef.current(...args)}
                        selectedAccountIds={(accounts ?? [])
                          .filter(({ account }) =>
                            (account?.groups ?? []).some((g) => g?.id === group?.id),
                          )
                          .map(({ account }) => account?.id)
                          .filter(Boolean)}
                      />
                    </Col>
                  </>
                ) : (
                  <>
                    <Col
                      flexGrow={0}
                      className={styles['account-name']}
                      data-content={account.login}
                    >
                      <Typography
                        variant={'Body-Forms-15'}
                        component={'div'}
                      >
                        {account.login}
                      </Typography>
                    </Col>
                    <Col
                      flexGrow={1}
                      className={styles['account-name__control-opener']}
                    >
                      <AccountManager
                        isDemoMode={isDemoMode}
                        account={account}
                        groups={groups}
                        onApply={(...args) => handleAccountAttachRef.current(...args)}
                      />
                    </Col>
                  </>
                )}
              </Grid>
            ),
          },
          {
            id: 'account.active',
            header: t('statistics::table.columns.header.status'),
            size: 192,
            enableHiding: false,
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original,
                depth,
              },
            }) => {
              if (depth === 0) {
                const statuses = uniq(original?.accounts?.map(({ account }) => account?.active))

                if (statuses.length === 1) {
                  return <StatusBadge status={statuses[0] ? 'new' : 'archived'} />
                } else {
                  if (statuses.length > 1) {
                    return (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {t('statistics::table.status.mixed')}
                      </span>
                    )
                  } else {
                    return null
                  }
                }
              } else {
                const active = original?.account?.active ?? original?.active

                return active === undefined || active === null ? null : (
                  <StatusBadge status={active ? 'new' : 'archived'} />
                )
              }
            },
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            size: 192,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            // footer: () => <b>{formatByLocale(summary.impressions)}</b>,
          },
          {
            id: filters.include_vat ? 'cost_with_vat' : 'cost',
            header: t(
              'statistics::table.columns.header.' +
                (filters.include_vat ? 'cost_with_vat' : 'cost'),
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',
            size: 192,
            cell: ({ cell }) =>
              cell.getValue() === null ? (
                '—'
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatMoney(
                      formatByLocale(cell.getValue(), i18n, {
                        maximumFractionDigits: 2,
                      }),
                      'RUB',
                    ),
                  }}
                />
              ),
            // footer: () => (
            //   <b
            //     dangerouslySetInnerHTML={{
            //       __html: formatMoney(
            //         formatByLocale(summary[filters.include_vat ? 'cost_with_vat' : 'cost'], i18n, {
            //           maximumFractionDigits: 2,
            //         }),
            //         'RUB',
            //       ),
            //     }}
            //   />
            // ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue'),
            accessorKey: 'revenue',
            size: 192,
            cell: ({ cell }) =>
              cell.getValue() === null ? (
                '—'
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatMoney(
                      formatByLocale(cell.getValue(), i18n, {
                        maximumFractionDigits: 2,
                      }),
                      'RUB',
                    ),
                  }}
                />
              ),
            // footer: () => (
            //   <b
            //     dangerouslySetInnerHTML={{
            //       __html: formatMoney(
            //         formatByLocale(summary.revenue, i18n, {
            //           maximumFractionDigits: 2,
            //         }),
            //         'RUB',
            //       ),
            //     }}
            //   />
            // ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            size: 192,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            // footer: () => <b>{formatByLocale(summary.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            size: 192,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }) + ' %',
            // footer: () => (
            //   <b>{formatByLocale(summary.ctr, i18n, { maximumFractionDigits: 2 }) + ' %'}</b>
            // ),
          },
          {
            id: 'avg_click_price',
            header: t('statistics::table.columns.header.avg_click_price'),
            tooltip: t('statistics::table.columns.header.avg_click_price_tooltip'),
            accessorKey: 'avg_click_price',
            size: 192,
            cell: ({ cell }) =>
              cell.getValue() === null ? (
                '—'
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatMoney(
                      formatByLocale(cell.getValue(), i18n, {
                        maximumFractionDigits: 2,
                      }),
                      'RUB',
                    ),
                  }}
                />
              ),
            // footer: () => (
            //   <b
            //     dangerouslySetInnerHTML={{
            //       __html: formatMoney(
            //         formatByLocale(summary.avg_click_price, i18n, {
            //           maximumFractionDigits: 2,
            //         }),
            //         'RUB',
            //       ),
            //     }}
            //   />
            // ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',
            size: 192,
            cell: ({ cell }) => (cell.getValue() === null ? '—' : formatByLocale(cell.getValue())),
            // footer: () => <b>{formatByLocale(summary.conversions)}</b>,
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',
            size: 192,
            cell: ({ cell }) =>
              cell.getValue() === null
                ? '—'
                : formatByLocale(cell.getValue(), i18n, {
                    maximumFractionDigits: 2,
                  }) + ' %',
            // footer: () => (
            //   <b>
            //     {formatByLocale(summary.conversion_rate, i18n, {
            //       maximumFractionDigits: 2,
            //     }) + ' %'}
            //   </b>
            // ),
          },
        ]
    }
  }, [groupBy, filters, i18n, userId, t, pathGen, groups, isDemoMode])

  return {
    columns,
    ...(groupBy === 'groups'
      ? {
          getRowCanExpand: (row) => row.depth === 0,
          getSubRows: (row) => row?.subRows || row?.accounts || [],
        }
      : {}),
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    showHeader: true,
    showFooter: groupBy !== 'groups',
    enableRowSelection: false,
    enableExpanding: groupBy === 'groups',
    className,
  }
}
